import { Fragment, useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";

import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { fetchVehicles } from "../../../api/vehicle";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { TWclassNames } from "../../../components/utils/Div";
import { PMPagination } from "../../../components/utils/PMPagination";

/**
 * Vehicle picker dialog. Uses a local fetch (not the global Redux vehicles slice) so the list is
 * not overwritten by concurrent requests from the main vehicles page.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onSelect — (vehicleId) => void
 * @param {Array<{name: string, value: *}>} [props.filters] — API filters (e.g. available_for_driver_checkin)
 * @param {boolean} [props.checkInMode] — copy for driver check-in flow
 * @param {boolean} [props.allowOccupiedVehicles] — admin: show active vehicles even if another driver is checked in
 */
export default function VehiclesListDialog(props) {
  const { t } = useTranslation();

  const {
    open,
    onClose,
    onSelect,
    filters: extraFilters = [],
    checkInMode = false,
    allowOccupiedVehicles = false,
  } = props;

  const [controller, setController] = useState({
    query: "",
    page: 1,
    per_page: 10,
    sortBy: "created_at",
    sort: "desc",
    filters: [],
    view: null,
  });

  const [listData, setListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);

  const filterKey = useMemo(() => JSON.stringify(extraFilters || []), [extraFilters]);
  const fetchKey = useMemo(
    () =>
      JSON.stringify({
        q: controller.query,
        page: controller.page,
        per_page: controller.per_page,
        sortBy: controller.sortBy,
        sort: controller.sort,
        filters: controller.filters,
      }),
    [
      controller.query,
      controller.page,
      controller.per_page,
      controller.sortBy,
      controller.sort,
      controller.filters,
    ]
  );

  const requestIdRef = useRef(0);

  useLayoutEffect(() => {
    if (!open) return;
    setController({
      query: "",
      page: 1,
      per_page: 10,
      sortBy: "created_at",
      sort: "desc",
      filters: Array.isArray(extraFilters) ? [...extraFilters] : [],
      view: null,
    });
  }, [open, filterKey, extraFilters]);

  const handlePageChange = (newPage) => {
    setController((c) => ({ ...c, page: newPage }));
  };

  useEffect(() => {
    if (!open) return undefined;

    const id = ++requestIdRef.current;
    setListLoading(true);

    fetchVehicles(controller)
      .then((res) => {
        if (requestIdRef.current !== id) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setListData(rows);
        setTotalCount(typeof res?.totalCount === "number" ? res.totalCount : rows.length);
        setCurrentCount(typeof res?.currentCount === "number" ? res.currentCount : rows.length);
      })
      .catch(() => {
        if (requestIdRef.current !== id) return;
        setListData([]);
        setTotalCount(0);
        setCurrentCount(0);
      })
      .finally(() => {
        if (requestIdRef.current === id) setListLoading(false);
      });

    return undefined;
  }, [open, fetchKey]);

  /** Driver self-service: only unoccupied active vehicles. Admin (allowOccupiedVehicles): all active vehicles. */
  const rowsToShow = useMemo(() => {
    if (!checkInMode) return listData || [];
    return (listData || []).filter((v) => {
      if (v?.active === false) return false;
      if (allowOccupiedVehicles) return true;
      if (v?.checked_in_date_time) return false;
      if (v?.checked_in_driver?.id != null) return false;
      return true;
    });
  }, [listData, checkInMode, allowOccupiedVehicles]);

  const title = checkInMode ? t("Check into a vehicle") : t("Vehicles");
  const emptyMsg = checkInMode
    ? allowOccupiedVehicles
      ? t("automile.noActiveVehiclesForFleet")
      : t("automile.noVehiclesAvailable")
    : t("No vehicles found");
  const showEmpty = !listLoading && (!listData?.length || rowsToShow.length === 0);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[3500]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[3500] flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg transform rounded-t-2xl bg-white text-left shadow-xl transition-all sm:rounded-2xl max-h-[min(92vh,100%)] flex flex-col sm:max-h-[85vh] pb-[max(0px,env(safe-area-inset-bottom))]">
              <div className="flex-shrink-0 border-b border-gray-100 px-4 py-4 sm:px-6">
                <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                  {title}
                </Dialog.Title>
                {checkInMode && (
                  <p className="mt-1 text-sm text-gray-500">
                    {allowOccupiedVehicles
                      ? t("automile.adminVehiclePickerSubtitle")
                      : t("automile.onlyAvailableVehiclesSubtitle")}
                  </p>
                )}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-2 sm:px-4">
                {listLoading ? (
                  <div className="py-12">
                    <ResourceLoading />
                  </div>
                ) : showEmpty ? (
                  <div className="py-10 px-4 text-center text-sm text-gray-500">{emptyMsg}</div>
                ) : (
                  <ul role="list" className="divide-y divide-gray-100 pb-2">
                    {rowsToShow.map((item) => (
                      <li
                        onClick={() => onSelect(item?.id)}
                        key={item.id}
                        className={TWclassNames(
                          "cursor-pointer relative flex justify-between gap-x-4 px-3 py-4 hover:bg-gray-50 rounded-lg sm:px-4"
                        )}
                      >
                        <div className="flex min-w-0 flex-1 gap-x-3">
                          <div className="min-w-0 flex-auto">
                            <p className="text-sm font-semibold leading-6 text-gray-900">
                              <span className="absolute inset-0" aria-hidden="true" />
                              {item?.displayTitle || item?.number_plate || "—"}
                            </p>
                            {item?.model && (
                              <p className="text-xs text-gray-500 truncate">{item.model}</p>
                            )}
                            {checkInMode &&
                              allowOccupiedVehicles &&
                              (item?.checked_in_driver?.id != null || item?.checked_in_date_time) && (
                                <p className="text-xs font-medium text-amber-700 mt-0.5">
                                  {t("automile.vehicleOccupiedBadge")}
                                </p>
                              )}
                          </div>
                        </div>
                        <ChevronRightIcon className="h-5 w-5 flex-none text-gray-400 self-center" aria-hidden="true" />
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex-shrink-0 border-t border-gray-100 px-2 py-3 sm:px-4">
                <PMPagination
                  disabled={listLoading}
                  onPageChange={handlePageChange}
                  page={controller?.page}
                  perPage={controller?.per_page}
                  totalCount={totalCount}
                  currentCount={currentCount}
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
